import { createApi } from "@reduxjs/toolkit/query/react";
import {
  ApplicationData,
  ApplicationInfo,
  ApplicationFields,
  ApplicationFiles,
  ApplicationFilter,
} from "features/application/types/application";
import { StatusHistory } from "shared/types/statuses";
import { UploadedFile } from "shared/types/dropzone";
import { baseAppQuery } from "shared/api/baseQuery";
import { formatDateUi } from "shared/utils/converters";
import { snackbarOnQueryStarted } from "middleware/snackbarOnQueryStarted";

const ENDPOINT = "/application";
const applicationApi: any = createApi({
  reducerPath: "applicationApi",
  baseQuery: baseAppQuery,
  keepUnusedDataFor: 0,
  refetchOnMountOrArgChange: true,
  tagTypes: ["application", "applicationFile"],
  endpoints: (builder) => ({
    getApplicationList: builder.query<ApplicationData, ApplicationFilter>({
      query: (filter) => {
        const queryParams = new URLSearchParams();
        if (filter) {
          queryParams.set("page", filter.pageNumber.toString());
          queryParams.set("size", filter.pageSize.toString());
          if(filter.orgName) queryParams.set('orgName', filter.orgName);
          if (filter.orgId) queryParams.set("orgId", filter.orgId);
          if (filter.debtorBin) queryParams.set("debtorBin", filter.debtorBin);
          if (filter.amount) queryParams.set("amount", filter.amount);

          if (filter.statusCode) {
            queryParams.set("statusCode", filter.statusCode);
          }
          if (filter.dateFrom) {
            queryParams.set("fromDate", formatDateUi(filter.dateFrom));
          }
          if (filter.dateTo) {
            queryParams.set("toDate", formatDateUi(filter.dateTo));
          }
        }

        return `${ENDPOINT}/fetch-all?${queryParams}`;
      },
      providesTags: ["application"],
    }),
    getApplicationById: builder.query<ApplicationData, string>({
      query: (id) => `${ENDPOINT}/${id}`,
      providesTags: ["application", "applicationFile"],
    }),
    getApplicationListCurrent: builder.query<
      ApplicationData,
      ApplicationFilter
    >({
      query: (filter) => {
        const queryParams = new URLSearchParams();
        if (filter) {
          queryParams.set("page", filter.pageNumber.toString());
          queryParams.set("size", filter.pageSize.toString());
          if(filter.orgName) queryParams.set('orgName', filter.orgName);
          if (filter.orgId) queryParams.set("orgId", filter.orgId);
          if (filter.debtorBin) queryParams.set("debtorBin", filter.debtorBin);
          if (filter.amount) queryParams.set("amount", filter.amount);

          if (filter.statusCode) {
            queryParams.set("statusCode", filter.statusCode);
          }
          if (filter.dateFrom) {
            queryParams.set("fromDate", formatDateUi(filter.dateFrom));
          }
          if (filter.dateTo) {
            queryParams.set("toDate", formatDateUi(filter.dateTo));
          }
        }
        return `${ENDPOINT}/fetch-all-current?${queryParams}`;
      },
      providesTags: ["application"],
    }),
    getApplicationByIdCurrent: builder.query<ApplicationData, string>({
      query: (id) => `${ENDPOINT}/current/${id}`,
      providesTags: ["application", "applicationFile"],
    }),
    getApplicationHistoryById: builder.query<StatusHistory[], string>({
      query: (id) => `${ENDPOINT}/status-history/${id}`,
      providesTags: ["application"],
    }),
    getApplicationDocumentListById: builder.query<ApplicationFiles[], string>({
      query: (id) => `${ENDPOINT}/document/list/${id}`,
      providesTags: ["applicationFile"],
    }),
    getApplicationDocumentListByDocumentTypeId: builder.query<ApplicationFiles[], {applicationId:string, documentTypeId:string}>({
      query:({applicationId, documentTypeId})=>`${ENDPOINT}/document-by-type/list/${applicationId}/${documentTypeId}`,
      providesTags:["application","applicationFile"],
    }),
    getApplicationAvailableActions: builder.query<string[], string>({
      query: (id) => `${ENDPOINT}/available-actions/${id}`,
      providesTags: ["application", "applicationFile"],
    }),
    getApplicationAvailableActionsBase: builder.query<string[], string>({
      query: (id) => `${ENDPOINT}/available-actions-base/${id}`,
       providesTags: ["application", "applicationFile"],
    }),
    createApplication: builder.mutation<ApplicationData, {factorId?:string}>({
      query: (data) => ({
        url: ENDPOINT,
        method: "POST",
        body: data
      }),
    }),
    rejectApplication: builder.mutation<
      ApplicationData,
      { id: string; comment: string }
    >({
      query: ({id:applicationId, comment}) => ({
        url: `${ENDPOINT}/reject`,
        method: "POST",
        body: {applicationId, comment},
      }),
      invalidatesTags: ["application"],
      onQueryStarted: snackbarOnQueryStarted({
        successMessage:"applicationRejectSuccess",
        errorMessage:"applicationRejectError",
      })
    }),
    revisionApplication: builder.mutation<
      ApplicationData,
      { id: string; comment: string }
    >({
      query: ({id:applicationId, comment}) => ({
        url: `${ENDPOINT}/revision`,
        method: "POST",
        body: {applicationId, comment},
      }),
      invalidatesTags: ["application"],
      onQueryStarted: snackbarOnQueryStarted({
        successMessage:"revisionSuccess",
        errorMessage:"revisionError",
      })
    }),
    initApplicationConfirmation: builder.mutation<ApplicationData, string>({
      query: (id)=>({
        url:`${ENDPOINT}/init-application-confirmation/${id}`,
        method: "POST",
      }),
      invalidatesTags: ["application"],
      onQueryStarted: snackbarOnQueryStarted({
        successMessage:"initApplicationConfirmationSuccess",
        errorMessage:"initApplicationConfirmationError",
      })
    }),
    updateApplication: builder.mutation<ApplicationData, ApplicationFields>({
      query: (data) => ({
        url: ENDPOINT,
        method: "PUT",
        body: data,
      }),
      onQueryStarted: snackbarOnQueryStarted({
        successMessage:"successFormAlert",
        errorMessage:"errorFormAlert",
      })
    }),
    deleteApplication: builder.mutation<ApplicationData, string>({
      query: (id) => ({
        url: `${ENDPOINT}/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: ["application"],
    }),
    downloadApplicationFiles: builder.query<UploadedFile, string>({
      query: (documentId) => ({
        url: `${ENDPOINT}/download/${documentId}`,
        responseHandler: (response) => response.blob().then((blob) => blob),
      }),
    }),
    uploadApplicationFiles: builder.mutation<
      ApplicationInfo,
      { applicationId: string; documentTypeId: string; files: File }
    >({
      query: ({ applicationId, documentTypeId, files }) => {
        let formData = new FormData();
        formData.append("files", files);
        return {
          url: `${ENDPOINT}/document/upload?applicationId=${applicationId}&documentTypeId=${documentTypeId}`,
          method: "POST",
          body: formData,
        };
      },
      invalidatesTags: ["applicationFile"],
    }),
    deleteApplicationFiles: builder.mutation<
      File,
      { applicationId: string; documentId: string }
    >({
      query: ({ applicationId, documentId }) => ({
        url: `${ENDPOINT}/document/delete?applicationId=${applicationId}&documentId=${documentId}`,
        method: "DELETE",
      }),
      invalidatesTags: ["applicationFile"],
      onQueryStarted: snackbarOnQueryStarted({
        successMessage:"filesRemoveSuccess",
        errorMessage:"filesRemoveError",
      })
    }),
    deleteApplicationFileByCurrent: builder.mutation<
      File,
      { applicationId: string; documentId: string }
    >({
      query: ({ applicationId, documentId }) => ({
        url: `${ENDPOINT}/document/delete-by-current?applicationId=${applicationId}&documentId=${documentId}`,
        method: "DELETE",
      }),
    }),
    
  }),
});

export const {
  useGetApplicationListQuery,
  useGetApplicationByIdQuery,
  useGetApplicationListCurrentQuery,
  useGetApplicationByIdCurrentQuery,
  useGetApplicationHistoryByIdQuery,
  useGetApplicationDocumentListByIdQuery,
  getApplicationDocumentListByDocumentTypeId,
  useGetApplicationAvailableActionsQuery,
  useGetApplicationAvailableActionsBaseQuery,
  useCreateApplicationMutation,
  useRejectApplicationMutation,
  useRevisionApplicationMutation,
  useInitApplicationConfirmationMutation,
  useUpdateApplicationMutation,
  useDeleteApplicationMutation,
  useDownloadApplicationFilesQuery,
  useLazyDownloadApplicationFilesQuery,
  useUploadApplicationFilesMutation,
  useDeleteApplicationFilesMutation,
} = applicationApi;
export default applicationApi;
